if (!window.customElements.get('custom-variant-static-toggle')) {
  window.customElements.define(
    'custom-variant-static-toggle',
    class CustomVariantStaticToggle extends window.HTMLElement {
      connectedCallback() {
        this.initDOMElements();
        this.initEventListeners();
        this.initStickyAddToCart();
        this.updateUI();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }

      initDOMElements() {
        this.form = this.querySelector('form');
        this.formContainer = this.querySelector('.custom-variant-picker-static__form-container') || this.querySelector('.custom-variant-picker-static-v2__form-container');
        this.deliveryInterval = this.querySelector('#current-delivery-interval');
        this.addToCartButton = this.querySelector('#add-to-cart-button');
        this.addToCartPrice = this.querySelector('#add-to-cart-subscription-price');
        this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
        this.oneTimeSwatch = this.querySelector('#swatch-item-one-time-purchase');
        this.subscribeSwatch = this.querySelector('#swatch-item-subscribe-and-save');
        this.optionUnitPrices = this.querySelectorAll('#option-unit-price');
        this.crossedPriceWrappers = this.querySelectorAll('#option-unit-price-wrapper');
        this.savingsWrappers = this.querySelectorAll('#savings-wrapper');
        this.savingsElements = this.querySelectorAll('#savings');
        this.pauseOrCancelAnytimeText = this.querySelector('#pause-or-cancel-anytime');
        this.buyOnceForm = this.querySelector('#buy-once-form');
        this.buyOnceButton = this.querySelector('#buy-once-button');
        this.buyOncePrice = this.querySelector('#buy-once-price');
        this.badgeElement = this.querySelector('.custom-variant-picker-static-v2__badge');
        this.dynamicBulletOneText = this.querySelector('#dynamic-bullet-one-text');
        this.dynamicBulletOneTextOneTime = this.querySelector('#dynamic-bullet-one-text-one-time');
        this.enableGtmTracking = this.dataset.enableGtmTracking === 'true';
      }

      initEventListeners() {
        this.boundHandleFormChange = this.handleFormChange.bind(this);
        this.boundHandleOneTimeClick = this.handleOneTimeClick.bind(this);
        this.boundHandleSubscriptionClick = this.handleSubscriptionClick.bind(this);

        this.form?.addEventListener('change', this.boundHandleFormChange);
        this.oneTimeSwatch?.addEventListener('click', this.boundHandleOneTimeClick);
        this.subscribeSwatch?.addEventListener('click', this.boundHandleSubscriptionClick);
      }

      removeEventListeners() {
        this.form?.removeEventListener('change', this.boundHandleFormChange);
        this.oneTimeSwatch?.removeEventListener('click', this.boundHandleOneTimeClick);
        this.subscribeSwatch?.removeEventListener('click', this.boundHandleSubscriptionClick);
      }

      handleFormChange() {
        this.updateUI();
      }

      handleOneTimeClick(event) {
        event.preventDefault();
        this.activatePaymentType('one-time');
      }

      handleSubscriptionClick(event) {
        event.preventDefault();
        this.activatePaymentType('subscription');
      }

      activatePaymentType(type) {
        if (type === 'one-time') {
          this.oneTimeSwatch?.classList.add('active');
          this.subscribeSwatch?.classList.remove('active');
          this.formContainer?.classList.add('one-time-selected');
        } else {
          this.oneTimeSwatch?.classList.remove('active');
          this.subscribeSwatch?.classList.add('active');
          this.formContainer?.classList.remove('one-time-selected');
        }
        this.updateUI();
      }

      updateUI() {
        const selectedVariant = this.getSelectedVariant();

        if (!selectedVariant) {
          return;
        }

        const isOneTime = this.isOneTimeActive();

        this.formContainer?.classList.toggle('one-time-selected', isOneTime);

        this.updateDeliveryInterval(selectedVariant);
        this.updateMainButton(selectedVariant, isOneTime);
        this.updateBuyOnceLink(selectedVariant, isOneTime);
        this.updateAllOptions(isOneTime);
        this.updateFooterText(isOneTime);
        this.updateBadgeVisibility(isOneTime);
        this.updateDynamicBulletText(selectedVariant);
        this.updateStickyAddToCart(selectedVariant, isOneTime);
      }

      updateDeliveryInterval(selectedVariant) {
        if (this.deliveryInterval) {
          this.deliveryInterval.textContent = selectedVariant.dataset.deliveryInterval || '';
        }
      }

      updateMainButton(selectedVariant, isOneTime) {
        const data = selectedVariant.dataset;

        if (this.addToCartButton) {
          const targetUrl = isOneTime ? data.buyOnceUrl : data.productUrl;
          this.addToCartButton.href = this.appendTrackingParams(targetUrl);
        }

        const currentPrice = isOneTime ? data.buyOnePrice : data.price;
        const comparePrice = data.compareAtPrice;

        if (this.addToCartPrice) {
          this.addToCartPrice.textContent = currentPrice;
        }

        if (this.addToCartCompareAtPrice) {
          if (comparePrice && comparePrice !== currentPrice) {
            this.addToCartCompareAtPrice.textContent = comparePrice;
            this.addToCartCompareAtPrice.classList.remove('hidden');
          } else {
            this.addToCartCompareAtPrice.classList.add('hidden');
          }
        }
      }

      updateAllOptions(isOneTime) {
        const allInputs = this.form?.querySelectorAll('input[type="radio"]') || [];

        allInputs.forEach((input, index) => {
          this.updateSingleOption(input, index, isOneTime);
        });
      }

      updateSingleOption(input, index, isOneTime) {
        const data = input.dataset;
        const priceEl = this.optionUnitPrices[index];
        const crossedWrapper = this.crossedPriceWrappers[index];
        const savingsWrapper = this.savingsWrappers[index];
        const savingsEl = this.savingsElements[index];

        if (isOneTime) {
          if (priceEl) priceEl.textContent = data.unitCompareAtPrice || '';
          const hasSavings = data.savingsOneTime;
          crossedWrapper?.classList.toggle('hidden', !hasSavings);
          savingsWrapper?.classList.toggle('hidden', !hasSavings);
          if (savingsEl && hasSavings) savingsEl.textContent = hasSavings;
        } else {
          if (priceEl) priceEl.textContent = data.unitPrice || '';
          const hasSavings = data.savings;
          crossedWrapper?.classList.toggle('hidden', !hasSavings);
          savingsWrapper?.classList.toggle('hidden', !hasSavings);
          if (savingsEl && hasSavings) savingsEl.textContent = hasSavings;
        }
      }

      getSelectedVariant() {
        return this.form?.querySelector('input[type="radio"]:checked');
      }

      getVariantLabel(selectedVariant) {
        return (
          selectedVariant
            .closest('.custom-variant-picker-static__option, .custom-variant-picker-static-v2__option')
            ?.querySelector(
              '.custom-variant-picker-static__option-custom-label, .custom-variant-picker-static-v2__option-custom-label',
            )
            ?.textContent?.trim() || ''
        );
      }

      isOneTimeActive() {
        return this.oneTimeSwatch?.classList.contains('active') ?? true;
      }

      updateDynamicBulletText(selectedVariant) {
        if (this.dynamicBulletOneText) {
          const bulletText = selectedVariant.dataset.bulletOneText;
          if (bulletText) {
            this.dynamicBulletOneText.textContent = bulletText;
          }
        }

        if (this.dynamicBulletOneTextOneTime) {
          const oneTimeBulletText = selectedVariant.dataset.oneTimeBulletOneText;
          if (oneTimeBulletText) {
            this.dynamicBulletOneTextOneTime.textContent = oneTimeBulletText;
          }
        }
      }

      updateBadgeVisibility(isOneTime) {
        if (this.badgeElement) {
          this.badgeElement.classList.toggle('hidden', isOneTime);
        }
      }

      updateBuyOnceLink(selectedVariant, isOneTime) {
        const data = selectedVariant.dataset;

        if (this.buyOnceButton && data.buyOnceUrl) {
          this.buyOnceButton.href = this.appendTrackingParams(data.buyOnceUrl);
        }

        if (this.buyOncePrice) {
          this.buyOncePrice.textContent = data.buyOnePrice || '';
        }

        if (this.buyOnceForm) {
          this.buyOnceForm.classList.toggle('hidden', isOneTime || !data.buyOnceUrl);
        }
      }

      updateFooterText(isOneTime) {
        if (this.pauseOrCancelAnytimeText) {
          if (isOneTime) {
            this.pauseOrCancelAnytimeText.classList.add('hidden');
          } else {
            this.pauseOrCancelAnytimeText.classList.remove('hidden');
          }
        }
      }

      appendTrackingParams(url) {
        if (!this.enableGtmTracking || !url || !window.SeranovaTracking) {
          return url;
        }

        return window.SeranovaTracking.appendParams(url);
      }

      initStickyAddToCart() {
        customElements.whenDefined('custom-sticky-add-to-cart').then(() => {
          this.stickyAddToCart = document.querySelector('custom-sticky-add-to-cart');
          const stickyForm = this.stickyAddToCart?.querySelector('#sticky-add-to-cart-form');
          const stickySelect = this.stickyAddToCart?.querySelector('#sticky-variant-select');

          if (!stickyForm || stickyForm.dataset.staticPickerBound) {
            return;
          }

          stickyForm.dataset.staticPickerBound = 'true';
          this.stickyAddToCart.querySelector('#sticky-buy-once-form')?.classList.add('hidden');
          this.buildStickySelectOptions(stickySelect);

          const stickyButtonLabel = this.stickyAddToCart.querySelector('#sticky-add-to-cart-button-label');
          const mainButtonLabel = this.querySelector('#add-to-cart-button-label');
          if (stickyButtonLabel && mainButtonLabel) {
            stickyButtonLabel.textContent = mainButtonLabel.textContent.trim();
          }

          stickySelect?.addEventListener('change', (event) => {
            event.stopPropagation();

            const radios = this.form?.querySelectorAll('input[type="radio"]');
            const radio = radios?.[Number(event.target.value)];

            if (!radio) {
              return;
            }

            radio.checked = true;
            this.updateUI();
          });

          stickyForm.addEventListener(
            'submit',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const href = this.addToCartButton?.getAttribute('href');

              if (href) {
                window.location.href = href;
              }
            },
            true,
          );

          const selectedVariant = this.getSelectedVariant();

          if (selectedVariant) {
            this.updateStickyAddToCart(selectedVariant, this.isOneTimeActive());
          }
        });

        customElements.whenDefined('custom-sticky-add-to-cart-one-time').then(() => {
          this.stickyAddToCart = document.querySelector('custom-sticky-add-to-cart-one-time');
          const stickyForm = this.stickyAddToCart?.querySelector('form');
          const stickySelect = this.stickyAddToCart?.querySelector('#sticky-variant-select');

          if (!stickyForm || stickyForm.dataset.staticPickerBound) {
            return;
          }

          stickyForm.dataset.staticPickerBound = 'true';
          this.buildStickySelectOptions(stickySelect);

          const stickyButtonLabel = this.stickyAddToCart.querySelector('#sticky-add-to-cart-button-label');
          const mainButtonLabel = this.querySelector('#add-to-cart-button-label');
          if (stickyButtonLabel && mainButtonLabel) {
            stickyButtonLabel.textContent = mainButtonLabel.textContent.trim();
          }

          stickySelect?.addEventListener('change', (event) => {
            event.stopPropagation();

            const radios = this.form?.querySelectorAll('input[type="radio"]');
            const radio = radios?.[Number(event.target.value)];

            if (!radio) {
              return;
            }

            radio.checked = true;
            this.updateUI();
          });

          stickyForm.addEventListener(
            'submit',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const href = this.addToCartButton?.getAttribute('href');

              if (href) {
                window.location.href = href;
              }
            },
            true,
          );

          const selectedVariant = this.getSelectedVariant();

          if (selectedVariant) {
            this.updateStickyAddToCart(selectedVariant, this.isOneTimeActive());
          }
        });
      }

      buildStickySelectOptions(stickySelect) {
        const radios = this.form?.querySelectorAll('input[type="radio"]');

        if (!radios?.length || !stickySelect) {
          return;
        }

        stickySelect.innerHTML = '';

        radios.forEach((radio, index) => {
          const label = this.getVariantLabel(radio) || `Option ${index + 1}`;
          const data = radio.dataset;
          const option = document.createElement('option');

          option.value = String(index);
          option.textContent = label;
          option.dataset.currentLabel = label;
          option.dataset.available = 'true';
          option.dataset.subscriptionPrice = data.price || '';
          option.dataset.price = data.buyOnePrice || '';
          option.dataset.compareAtPrice = data.compareAtPrice || '';

          if (radio.checked) {
            option.selected = true;
          }

          stickySelect.appendChild(option);
        });
      }

      updateStickyAddToCart(selectedVariant, isOneTime) {
        if (!this.stickyAddToCart) {
          return;
        }

        const data = selectedVariant.dataset;
        const label = this.getVariantLabel(selectedVariant);
        const currentPrice = isOneTime ? data.buyOnePrice : data.price;
        const comparePrice = data.compareAtPrice;

        const stickyVariantLabel = this.stickyAddToCart.querySelector('#sticky-variant-label');
        const stickyPrice =
          this.stickyAddToCart.querySelector('#sticky-add-to-cart-price') ||
          this.stickyAddToCart.querySelector('#sticky-price');
        const stickyCompareAtPrice =
          this.stickyAddToCart.querySelector('#sticky-add-to-cart-compare-at-price') ||
          this.stickyAddToCart.querySelector('#sticky-compare-at-price');
        const stickySelect = this.stickyAddToCart.querySelector('#sticky-variant-select');
        const radios = this.form?.querySelectorAll('input[type="radio"]');
        const selectedIndex = radios ? Array.from(radios).indexOf(selectedVariant) : -1;

        if (stickyVariantLabel) {
          stickyVariantLabel.textContent = label;
        }

        if (stickySelect && selectedIndex >= 0) {
          stickySelect.selectedIndex = selectedIndex;
        }

        if (stickyPrice && currentPrice) {
          stickyPrice.textContent = currentPrice;
        }

        if (stickyCompareAtPrice) {
          if (comparePrice && comparePrice !== currentPrice) {
            stickyCompareAtPrice.textContent = comparePrice;
            stickyCompareAtPrice.classList.remove('hidden');
          } else {
            stickyCompareAtPrice.classList.add('hidden');
          }
        }
      }
    },
  );
}
